import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { NavigationStart, Router } from '@angular/router';
import { defer, Observable, of } from 'rxjs';
import { catchError, filter, map, shareReplay } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { BaseService } from '../base/base.service';
import { SessionUserHashManagerService } from '../session-user-hash-manager/session-user-hash-manager.service';
import { MonitoringService } from '../monitoring/monitoring.service';
import {
  FeatureSwitch, FeatureSwitchHistoryEntry, FeatureSwitchStage, FeatureSwitchStageRequest, FeatureSwitchTurnOffRequest,
  FeatureSwitchTurnOffResult, FeatureSwitchUpdateRequest
} from '../../_models/admin/feature-switch.models';

/**
 * Typed calls to the feature switch endpoints: the admin list/save/history (api/Maintenance/FeatureSwitches...)
 * and the any-user read (api/FeatureSwitches/Enabled) behind isEnabled().
 *
 * The Enabled list is the set of switches on FOR THIS USER — the API applies targeting and rollout — so
 * each time it is fetched it is also stamped on App Insights as the global custom property `features`,
 * and all later telemetry carries the user's variant.
 *
 * Follows the shared BaseService pattern. prepareOptions() hashes
 * JSON.stringify(payload).toLowerCase() into the RequestMatch header, so the object passed to
 * prepareOptions MUST be the exact object sent as the request body. GETs send no body and use
 * prepareOptions(null). prepareOptions() also sets observe:'response', so every response is unwrapped.
 */
@Injectable({ providedIn: 'root' })
export class FeatureSwitchService extends BaseService {

  private apiUrl: string = environment.apiUrl;

  /**
   * The enabled keys for the current page load, shared by every isEnabled() caller. Dropped on each
   * NavigationStart so the next page refetches: that is how a toggle takes effect on the next page
   * load without signing in again.
   */
  private enabledKeys$: Observable<Set<string>> | null = null;

  constructor(
    private http: HttpClient,
    protected hashManagerService: SessionUserHashManagerService,
    private router: Router,
    private monitoring: MonitoringService
  ) {
    super(hashManagerService);
    this.apiUrl += this.apiUrl.endsWith('/') ? '' : '/';

    // Root service: lives for the app's lifetime, so this subscription is never torn down.
    this.router.events
      .pipe(filter(e => e instanceof NavigationStart))
      .subscribe(() => this.enabledKeys$ = null);
  }

  /**
   * Whether a feature switch is on. Fetches api/FeatureSwitches/Enabled at most once per page load.
   * Fails closed: any error (including one raised while signing the request) means false, and that
   * answer holds until the next navigation.
   */
  public isEnabled(key: string): Observable<boolean> {
    const wanted = (key || '').trim().toLowerCase();
    return this.getEnabledKeys().pipe(map(keys => wanted !== '' && keys.has(wanted)));
  }

  public getSwitches(): Observable<FeatureSwitch[]> {
    return this.http.get(this.apiUrl + 'api/Maintenance/FeatureSwitches', this.prepareOptions(null)).pipe(
      map((data: any) => this.unwrap(data) as FeatureSwitch[]),
      catchError(this.handleError('getSwitches'))
    );
  }

  /** Saves a switch's FULL settings (the PUT replaces every one) and returns the row as it now stands. */
  public saveSwitch(switchKey: string, body: FeatureSwitchUpdateRequest): Observable<FeatureSwitch> {
    return this.http.put(
      this.apiUrl + 'api/Maintenance/FeatureSwitches/' + encodeURIComponent(switchKey),
      body,
      this.prepareOptions(body)
    ).pipe(
      map((data: any) => this.unwrap(data) as FeatureSwitch),
      catchError(this.handleError('saveSwitch'))
    );
  }

  /**
   * Moves a switch to a lifecycle stage and returns the row as it now stands. Admin only; the API returns
   * 404 while featureswitches.lifecycle is off.
   */
  public setStage(switchKey: string, stage: FeatureSwitchStage): Observable<FeatureSwitch> {
    const body: FeatureSwitchStageRequest = { stage };
    return this.http.post(
      this.apiUrl + 'api/Maintenance/FeatureSwitches/' + encodeURIComponent(switchKey) + '/stage',
      body,
      this.prepareOptions(body)
    ).pipe(
      map((data: any) => this.unwrap(data) as FeatureSwitch),
      catchError(this.handleError('setStage'))
    );
  }

  /**
   * Turns features off in bulk (never on). With dryRun it only lists what would go off; without, it turns
   * them off and lists what did. The API returns 404 while featureswitches.bulk-off is off.
   */
  public turnOff(body: FeatureSwitchTurnOffRequest): Observable<FeatureSwitchTurnOffResult> {
    return this.http.post(this.apiUrl + 'api/FeatureSwitches/TurnOff', body, this.prepareOptions(body)).pipe(
      map((data: any) => this.unwrap(data) as FeatureSwitchTurnOffResult),
      catchError(this.handleError('turnOff'))
    );
  }

  /** The latest 50 changes to a switch, newest first. */
  public getHistory(switchKey: string): Observable<FeatureSwitchHistoryEntry[]> {
    return this.http.get(
      this.apiUrl + 'api/Maintenance/FeatureSwitches/' + encodeURIComponent(switchKey) + '/history',
      this.prepareOptions(null)
    ).pipe(
      map((data: any) => (this.unwrap(data) || []) as FeatureSwitchHistoryEntry[]),
      catchError(this.handleError('getHistory'))
    );
  }

  private getEnabledKeys(): Observable<Set<string>> {
    if (!this.enabledKeys$) {
      this.enabledKeys$ = defer(() =>
        this.http.get(this.apiUrl + 'api/FeatureSwitches/Enabled', this.prepareOptions(null))
      ).pipe(
        map((data: any) => {
          const body = this.unwrap(data);
          const keys: string[] = Array.isArray(body) ? body : [];
          const set = new Set(keys.filter(k => typeof k === 'string').map(k => k.trim().toLowerCase()));
          this.recordVariant(set);
          return set;
        }),
        catchError(() => of(new Set<string>())),
        shareReplay(1)
      );
    }
    return this.enabledKeys$;
  }

  /**
   * Stamps the user's variant — the sorted, comma-joined keys on for them — on all later telemetry.
   * Only after a successful fetch: a failed one says nothing about the variant, so the last known value
   * stands. Telemetry must never break a screen, so an error here is swallowed.
   */
  private recordVariant(keys: Set<string>): void {
    try {
      this.monitoring.setGlobalProperty('features', Array.from(keys).sort().join(','));
    } catch {
      // Best effort only.
    }
  }

  /** Shared body unwrap — prepareOptions() observes the full response, not just the body. */
  private unwrap(data: any): any {
    return data ? data.body : data;
  }
}
